import { Room, RoomEvent, Track } from "livekit-client";

class Livekit {
    constructor() {
        this.room = null;
        this.is_connected = false;
        this.callbacks = {
            on_connected: null,
            on_disconnected: null,
            on_error: null,
            on_chat_message: null,
            on_stop_exam: null,
        };
    }

    init(options = {}) {
        if (options.callbacks) {
            // Handle both for compatibility during transition
            this.callbacks.on_connected =
                options.callbacks.on_connected || options.callbacks.onConnected;
            this.callbacks.on_disconnected =
                options.callbacks.on_disconnected ||
                options.callbacks.onDisconnected;
            this.callbacks.on_error =
                options.callbacks.on_error || options.callbacks.onError;
            this.callbacks.on_chat_message =
                options.callbacks.on_chat_message || options.callbacks.onChatMessage;
            this.callbacks.on_stop_exam =
                options.callbacks.on_stop_exam || options.callbacks.onStopExam;
        }
    }

    async start(camera_stream, screen_stream, token, server_url) {
        try {
            if (!token || !server_url) {
                throw new Error("Token and server_url are required");
            }

            // Create Room instance - no simulcast, fixed 720p for both live and record
            this.room = new Room({
                adaptiveStream: true,
                dynacast: true,
                publishDefaults: {
                    simulcast: false,
                    videoCodec: "h264",
                    resolution: { width: 1280, height: 720 },  // 720p fixed
                },
            });

            // Set up event handlers
            this.room.on(RoomEvent.Connected, () => {
                this.is_connected = true;
                console.log("[LiveKit] Connected to room");
                this.callbacks.on_connected?.();
            });

            this.room.on(RoomEvent.Disconnected, () => {
                this.is_connected = false;
                console.log("[LiveKit] Disconnected from room");
                this.callbacks.on_disconnected?.();
            });

            this.room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
                try {
                    const decoder = new TextDecoder();
                    const strData = decoder.decode(payload);
                    const data = JSON.parse(strData);

                    if (data.type === "CHAT_MESSAGE") {
                        this.callbacks.on_chat_message?.(data.data);
                    } else if (data.type === "STOP_EXAM") {
                        console.log("[LiveKit] Received STOP_EXAM command");
                        this.callbacks.on_stop_exam?.(data.data);
                    }
                } catch (err) {
                    console.warn("[LiveKit] Failed to parse data message:", err);
                }
            });

            // Connect to room
            await this.room.connect(server_url, token);

            // Publish camera track
            if (camera_stream) {
                const video_track = camera_stream.getVideoTracks()[0];
                if (video_track) {
                    await this.room.localParticipant.publishTrack(video_track, {
                        name: "camera",
                        source: Track.Source.Camera,
                    });
                    console.log("[LiveKit] Camera track published");
                }

                const audio_track = camera_stream.getAudioTracks()[0];
                if (audio_track) {
                    await this.room.localParticipant.publishTrack(audio_track, {
                        name: "microphone",
                        source: Track.Source.Microphone,
                    });
                    console.log("[LiveKit] Audio track published");
                }
            }

            // Publish screen track (if available)
            if (screen_stream) {
                const screen_video_track = screen_stream.getVideoTracks()[0];
                if (screen_video_track) {
                    await this.room.localParticipant.publishTrack(
                        screen_video_track,
                        {
                            name: "screen",
                            source: Track.Source.ScreenShare,
                        },
                    );
                    console.log("[LiveKit] Screen track published");
                }
            }
        } catch (error) {
            console.error("[LiveKit] Failed to start:", error);
            this.callbacks.on_error?.(error);
            throw error;
        }
    }

    stop() {
        if (this.room) {
            this.room.disconnect();
            this.room = null;
            this.is_connected = false;
            console.log("[LiveKit] Stopped");
        }
    }

    /**
     * Republish camera and microphone tracks after recovery
     * @param {MediaStream} camera_stream - New camera stream
     */
    async republishCamera(camera_stream) {
        if (!this.room || !this.is_connected) {
            console.warn("[LiveKit] Cannot republish - not connected");
            return;
        }

        try {
            const local = this.room.localParticipant;

            // Unpublish existing camera and mic tracks
            const tracks_to_unpublish = [];
            local.trackPublications.forEach((pub) => {
                if (
                    pub.source === Track.Source.Camera ||
                    pub.source === Track.Source.Microphone
                ) {
                    tracks_to_unpublish.push(pub);
                }
            });

            for (const pub of tracks_to_unpublish) {
                if (pub.track) {
                    await local.unpublishTrack(pub.track);
                    console.log(`[LiveKit] Unpublished ${pub.source} track`);
                }
            }

            // Publish new tracks
            if (camera_stream) {
                const video_track = camera_stream.getVideoTracks()[0];
                if (video_track) {
                    await local.publishTrack(video_track, {
                        name: "camera",
                        source: Track.Source.Camera,
                    });
                    console.log("[LiveKit] Camera track republished");
                }

                const audio_track = camera_stream.getAudioTracks()[0];
                if (audio_track) {
                    await local.publishTrack(audio_track, {
                        name: "microphone",
                        source: Track.Source.Microphone,
                    });
                    console.log("[LiveKit] Audio track republished");
                }
            }
        } catch (error) {
            console.error("[LiveKit] Failed to republish camera:", error);
            this.callbacks.on_error?.(error);
        }
    }

    /**
     * Republish screen share track after recovery
     * @param {MediaStream} screen_stream - New screen stream
     */
    async republishScreen(screen_stream) {
        if (!this.room || !this.is_connected) {
            console.warn("[LiveKit] Cannot republish - not connected");
            return;
        }

        try {
            const local = this.room.localParticipant;

            // Unpublish existing screen track
            const tracks_to_unpublish = [];
            local.trackPublications.forEach((pub) => {
                if (pub.source === Track.Source.ScreenShare) {
                    tracks_to_unpublish.push(pub);
                }
            });

            for (const pub of tracks_to_unpublish) {
                if (pub.track) {
                    await local.unpublishTrack(pub.track);
                    console.log("[LiveKit] Unpublished screen track");
                }
            }

            // Publish new screen track
            if (screen_stream) {
                const screen_video_track = screen_stream.getVideoTracks()[0];
                if (screen_video_track) {
                    await local.publishTrack(screen_video_track, {
                        name: "screen",
                        source: Track.Source.ScreenShare,
                    });
                    console.log("[LiveKit] Screen track republished");
                }
            }
        } catch (error) {
            console.error("[LiveKit] Failed to republish screen:", error);
            this.callbacks.on_error?.(error);
        }
    }
}

export default new Livekit();
